import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { mobileUserInfoService } from '../services/mobileUserInfoService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';
import ConfirmationDialog from '../../shared/ui/ConfirmationDialog';
import Footer from '../components/Footer';
import UserInfoUpdateModal from '../components/UserInfoUpdateModal';
import { useAuth } from '../../shared/auth';

// CSS-in-JS スタイル
const styles = {
  container: {
    minHeight: '100vh',
    background: 'white',
    position: 'relative',
    paddingBottom: '60px'
  },
    // ヘッダー追加（オブジェクト画面と同様、IDなし）
    detailHeader: {
      width: '100%',
      height: '40px',
      background: '#444',
      position: 'fixed',
      top: 0,
      zIndex: 1000
    },
    headerLeft: {
      float: 'left'
    },
    showList: {
      width: '32px',
      height: '32px',
      margin: '5px 6px',
      display: 'inline-block',
      backgroundImage: 'url("/core/img/icon/back-arrow-button.png")',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'contain',
      float: 'left',
      cursor: 'pointer'
    },
  userInfoBody: {
    background: '#FFF',
    marginTop: '60px',
    textAlign: 'center',
    padding: '0 20px'
  },
  profileImageContainer: {
    margin: '0 auto 30px',
    width: '90px',
    height: '90px',
    border: '1px solid #000000',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5'
  },
  profileImage: {
    width: '88px',
    height: '88px',
    objectFit: 'cover'
  },
  noImageText: {
    fontSize: '10px',
    color: '#666',
    textAlign: 'center'
  },
  userInfoTable: {
    width: '95%',
    margin: '30px auto',
    borderCollapse: 'collapse'
  },
  tableTh: {
    width: '90px',
    height: '40px',
    fontSize: '12px',
    background: '#ece6e6',
    border: '1px solid #000000',
    padding: '8px',
    textAlign: 'left',
    verticalAlign: 'middle',
    fontWeight: 'bold'
  },
  tableTd: {
    height: '40px',
    fontSize: '14px',
    border: '1px solid #000000',
    padding: '8px',
    textAlign: 'left',
    fontWeight: 'bold'
  },
  actionButtons: {
    padding: '0 20px',
    marginTop: '30px'
  },
  actionButton: {
    height: '32px',
    width: '90%',
    margin: '12px auto',
    background: '#2B2222',
    color: '#ffffff',
    border: 'none',
    borderRadius: '16px',
    fontSize: '16px',
    letterSpacing: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

const AppUserInfo = () => {
  const navigate = useNavigate();

  const { user } = useAuth();

  const handleBack = () => {
    navigate('/appConfig');
  };
  
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false));
  
  // UI状態
  const [isLoading, setIsLoading] = useState(true); // ローディング状態
  const [isProcessing, setIsProcessing] = useState(false); // 処理中状態
  
  // ユーザー情報
  const [userInfo, setUserInfo] = useState({
    userName: '', // ユーザー名
    mailAddress: '', // メールアドレス
    riyoKyoten: '', // 利用拠点
    riyoKengen: null, // 利用権限
    profileImageId: null, // プロフィール画像ID
    filenameExtension: null // ファイル拡張子
  });
  
  // モーダル・ダイアログ状態
  const [showUpdateModal, setShowUpdateModal] = useState(false); // 更新モーダル表示フラグ
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // 削除確認ダイアログ表示フラグ
  
  // プロフィール画像
  const [profileImageUrl, setProfileImageUrl] = useState('/core/img/noprofileimage.png'); // プロフィール画像URL

  // コンポーネント初期化
  useEffect(() => {
    loadUserInfo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ユーザー情報読み込み処理
  const loadUserInfo = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await mobileUserInfoService.getPixUser(user);

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', 'ユーザー情報の読み込みに失敗しました。', null, 'error');
      }
      
      if (result.pixUserList && result.pixUserList.length > 0) {
        const userInfo = result.pixUserList[0];
        setUserInfo({
          userName: userInfo.userName || '',
          mailAddress: userInfo.mailAddress || '',
          riyoKyoten: userInfo.riyoKyoten || '',
          riyoKengen: userInfo.userAuthority,
          profileImageId: userInfo.profileImageId,
          filenameExtension: userInfo.filenameExtension
        });
        // プロフィール画像の読み込み
        if (userInfo.profileImageId) {
          try {
            const imageUrl = await mobileUserInfoService.getProfileImage(
              userInfo.pixUserId,
              userInfo.profileImageId,
              userInfo.filenameExtension,
              user
            );
            setProfileImageUrl(imageUrl);
          } catch (error) {
            setProfileImageUrl('/core/img/noprofileimage.png');
          }
        } else {
          setProfileImageUrl('/core/img/noprofileimage.png');
        }
      }
    } catch (error) {
      handleApiError(new Error('ユーザー情報の読み込みに失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError, user]);

  // ユーザー情報更新処理
  const handleUserInfoUpdate = async (formData) => {
    try {
      setIsProcessing(true);
      
      // パスワードの一致チェック
      if (formData.loginPassword && formData.loginPassword.trim() !== '') {
        if (formData.loginPassword !== formData.loginPassword2) {
          MessageDialog.show('エラー', '確認用パスワードが一致しません。', null, 'error');
          return;
        }
      }
      
      // メールアドレスの重複チェック（変更がある場合のみ）
      if (formData.mailAddress !== userInfo.mailAddress) {
        const countResult = await mobileUserInfoService.getPixUserCount(formData.mailAddress);
        
        if(countResult.returnCode !== '200'){
          MessageDialog.show('エラー', 'メールアドレスの重複チェックに失敗しました。', null, 'error');
          return;
        }

        if (countResult.pixUserCount >= 1) {
          MessageDialog.show('エラー', 'このメールアドレスは既に登録されています。', null, 'error');
          return;
        }
      }

      // ユーザー情報の更新
      const updateData = {
        userName: formData.userName,
        mailAddress: formData.mailAddress,
        loginPassword: formData.loginPassword || null,
        riyoKengen: userInfo.riyoKengen
      };

      const result = await mobileUserInfoService.updatePixUser(updateData, user);
      
      // ビジネスロジックエラーのチェック
      if (result.returnCode !== '200') {
        MessageDialog.show('エラー', 'ユーザー情報の更新に失敗しました。', null, 'error');
        setIsProcessing(false);
        return;
      }
      
      MessageDialog.show('メッセージ', '登録が完了しました。', null, 'success');
      setShowUpdateModal(false);
      await loadUserInfo(); // 再読み込み
    } catch (error) {
      handleApiError(new Error('ユーザー情報の更新に失敗しました。'));
    } finally {
      setIsProcessing(false);
    }
  };

  // アカウント削除処理
  const handleDeleteUser = async () => {
    try {
      setIsProcessing(true);
      const result = await mobileUserInfoService.deletePixUser(user);
      
      // ビジネスロジックエラーのチェック
      if (result.returnCode !== '200') {
        MessageDialog.show('エラー', 'アカウントの削除に失敗しました。', null, 'error');
        setIsProcessing(false);
        setShowDeleteConfirm(false);
        return;
      }
      
      MessageDialog.show('メッセージ', '削除が完了しました。ログイン画面に遷移します。', null, 'success');
      navigate('/appLogin');
    } catch (error) {
      handleApiError(new Error('アカウントの削除に失敗しました。'));
    } finally {
      setIsProcessing(false);
      setShowDeleteConfirm(false);
    }
  };

  // プロフィール画像レンダリング
  const renderProfileImage = () => {
    return (
      <img 
        src={profileImageUrl} 
        alt="プロフィール画像" 
        style={styles.profileImage}
        onError={() => setProfileImageUrl('/core/img/noprofileimage.png')}
      />
    );
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
          読み込み中...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
          {/* ヘッダー（戻るボタンのみ） */}
          <div style={styles.detailHeader}>
        <div style={styles.headerLeft}>
          <div
            style={styles.showList}
            onClick={handleBack}
            data-testid="back-button"
            role="button"
            aria-label="戻る"
          />
        </div>
      </div>
      {/* ユーザー情報ボディ */}
      <div style={styles.userInfoBody}>
        {/* プロフィール画像 */}
        <div style={styles.profileImageContainer}>
          {renderProfileImage()}
        </div>

        {/* ユーザー情報テーブル */}
        <table style={styles.userInfoTable}>
          <tbody>
            <tr>
              <th style={styles.tableTh}>ユーザー名</th>
              <td style={styles.tableTd}>{userInfo.userName}</td>
            </tr>
            <tr>
              <th style={styles.tableTh}>メールアドレス</th>
              <td style={styles.tableTd}>{userInfo.mailAddress}</td>
            </tr>
            <tr>
              <th style={styles.tableTh}>利用拠点</th>
              <td style={styles.tableTd}>{userInfo.riyoKyoten || ''}</td>
            </tr>
          </tbody>
        </table>

        {/* アクションボタン */}
        <div style={styles.actionButtons}>
          <button 
            style={styles.actionButton}
            onClick={() => setShowUpdateModal(true)}
            disabled={isProcessing}
            onMouseEnter={(e) => {
              if (!isProcessing) e.target.style.background = '#4a4a4a';
            }}
            onMouseLeave={(e) => {
              if (!isProcessing) e.target.style.background = '#2B2222';
            }}
          >
            ユーザー情報更新
          </button>
          
          <button 
            style={{
              ...styles.actionButton,
              background: '#2B2222'
            }}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isProcessing}
            onMouseEnter={(e) => {
              if (!isProcessing) e.target.style.background = '#4a4a4a';
            }}
            onMouseLeave={(e) => {
              if (!isProcessing) e.target.style.background = '#2B2222';
            }}
          >
            アカウント削除
          </button>
        </div>
      </div>

      {/* ボトムナビゲーション */}
      <Footer />

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />

      {/* アカウント削除確認ダイアログ */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteUser}
        onCancel={() => setShowDeleteConfirm(false)}
        title="確認"
        message="アカウントを削除しますか？"
        confirmText="OK"
        cancelText="キャンセル"
      />

      {/* ユーザー情報更新モーダル */}
      <UserInfoUpdateModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onUpdate={handleUserInfoUpdate}
        userInfo={userInfo}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export default AppUserInfo;

